import React, { useEffect, useState, useRef } from 'react'
import { Box, Button, Chip, Divider, IconButton, InputAdornment, MenuItem, Select, SelectChangeEvent, Skeleton, TextField, Tooltip, Typography } from '@mui/material'
import Grid from '@mui/material/Grid'
import ZDELines from './TimeCardLines'
import RestartAltIcon from '@mui/icons-material/RestartAlt'
import ContentPasteGoIcon from '@mui/icons-material/ContentPasteGo'
import { format, formatISO } from 'date-fns'
import { useTranslation } from 'react-i18next'
import { useDispatch, useSelector } from 'react-redux'
import { de } from 'date-fns/locale/de'
import { enUS } from 'date-fns/locale/en-US'
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns'
import { ITimecard, OnCallRequest, TimeCardLine } from '../../interface/ISelectedDate'
import { setSelectedDate } from '../../source/slice/SelectedDateSlice'
import { updateBreakLines } from '../../utils/functions/timeCardUtils'
import { IUser } from '../../interface/IUser'
import { DesktopTimePicker, LocalizationProvider } from '@mui/x-date-pickers'
import { setAlert } from '../../source/slice/AlertSlice'
import axios, { AxiosRequestConfig } from 'axios'
import { useMsal } from '@azure/msal-react'
import { ICustomSnackbar } from '../../interface/ICustomSnackbar'
import { UpdateStoreHelper } from '../../source/StoreHelper'
import ViolationComment from './ViolationComment'
import AddCircleIcon from '@mui/icons-material/AddCircle'
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline'
import CheckCircleIcon from '@mui/icons-material/CheckCircle'
import HistoryToggleOffIcon from '@mui/icons-material/HistoryToggleOff'
import { EmptyGuid, ENV_JID_BREAK, ENV_JID_START, ENV_JID_STOP, JID_RB, JID_SE } from '../../utils/values'
import { InteractionRequiredAuthError, SilentRequest } from '@azure/msal-browser'
import { setLoadingDate, setRequireCalendarReload } from '../../source/slice/LoadingDateSlice'
import { getAuthConfig } from '../../utils/authApi'
import { StatusChip } from '../../utils/StatusChip'
import Template from '../Template/Template'
import { setTemplates } from '../../source/slice/TemplateSlice'
import { fetchTemplates, applyTemplateToTimecard } from '../../utils/functions/templateUtils'
import { useUnsavedChanges } from '../ChangedDataWarning'
import { parseTimeAsLocal, createTimeString } from '../../utils/functions/timeUtils'

const ZDEHeader = () => {
    const { t, i18n } = useTranslation()
    const dispatch = useDispatch()
    const { instance, accounts } = useMsal()
    const { resetBaseline } = useUnsavedChanges()

    const selectedDate: ITimecard = useSelector((state: any) => state.selectedDate?.value)
    const user: IUser = useSelector((state: any) => state.user?.value)
    const loadingDate: boolean = useSelector((state: any) => state.loadingDate?.value)
    const [fixAmountDecimalsBreaks, setFixAmountDecimalsBreaks] = useState<boolean>(true)
    const [fixAmountDecimalsTravel, setFixAmountDecimalsTravel] = useState<boolean>(true)
    const [openTemplateDialog, setOpenTemplateDialog] = useState<boolean>(false)
    const isDayOpen = selectedDate.header?.status === 'OPEN'
    const templates = useSelector((state: any) => state.template?.value) || []

    let fromTime: string | undefined = selectedDate?.lines?.filter(
        (line: TimeCardLine) => line.mserp_timeandattendancejobid === ENV_JID_START && line.mserp_oncalltimeandattendancejobid !== JID_RB && line.mserp_oncalltimeandattendancejobid !== JID_SE
    )[0]?.mserp_startdatetime
    let toTime: string | undefined = selectedDate?.lines?.filter(
        (line: TimeCardLine) => line.mserp_timeandattendancejobid === ENV_JID_STOP && line.mserp_oncalltimeandattendancejobid !== JID_RB && line.mserp_oncalltimeandattendancejobid !== JID_SE
    )[0]?.mserp_stopdatetime

    const profileDateBase: Date = selectedDate?.header?.mserp_timeprofiledate
            ? new Date(selectedDate.header.mserp_timeprofiledate)
            : new Date()
    const localMidnight = (d: Date) => new Date(d.getFullYear(), d.getMonth(), d.getDate(), 0, 0, 0, 0)
    
    if (!fromTime && selectedDate?.header?.mserp_timeprofiledate) {
        fromTime = createTimeString(localMidnight(profileDateBase), selectedDate.header.mserp_timeprofiledate)
    }
    if (!toTime && selectedDate?.header?.mserp_timeprofiledate) {
        toTime = createTimeString(localMidnight(profileDateBase), selectedDate.header.mserp_timeprofiledate)
    }
    
    const fromTimeDate = parseTimeAsLocal(fromTime, selectedDate.header?.mserp_timeprofiledate)
    const toTimeDate = parseTimeAsLocal(toTime, selectedDate.header?.mserp_timeprofiledate)

    const breaks: TimeCardLine[] | undefined = selectedDate?.lines?.filter((line: TimeCardLine) => line.mserp_timeandattendancejobid === ENV_JID_BREAK && line.delete === false)

    const onCallAllowed: boolean = user.worker.mserp_mbrecordoncallduty
    const onCallValue: string | undefined = selectedDate?.lines?.filter((line: TimeCardLine) => line.mserp_oncalltimeandattendancejobid === JID_RB && line.mserp_timeandattendancejobid === ENV_JID_START)[0]?.mserp_mboncallapplicationid

    const getBreaksTime = () => {
        let breaksTime: number = 0.0
        breaks?.forEach((breakItem) => {
            if (breakItem.mserp_calculatedtimeseconds) breaksTime += breakItem.mserp_calculatedtimeseconds / 60 / 60
        })
        return breaksTime
    }

    let travelTime: number = 0
    const mbQuantityTravel = selectedDate?.header?.mserp_mbquantitytravel
    if (mbQuantityTravel) {
        travelTime = isNaN(mbQuantityTravel) ? 0 : mbQuantityTravel
    }

    let workTimeAccordingArbZG: number = 0.0
    if (fromTimeDate && toTimeDate) {
        workTimeAccordingArbZG = (toTimeDate.getTime() - fromTimeDate.getTime()) / 1000 / 60 / 60 - getBreaksTime() - travelTime
    }

    const handleDateChange = (jobId: string, fieldName: string) => (event: any) => {
        const currentDate = selectedDate.header?.mserp_timeprofiledate
        if (!isNaN(event?.getTime()) && currentDate !== undefined) {
            const currentDateTime = createTimeString(event, currentDate)
            handleLineChange(jobId, fieldName, currentDateTime)
        }
    }

    const appliedDateRef = useRef<string | null>(null)

    // Effect: load templates when msal instance/accounts become available
    useEffect(() => {
        let mounted = true
        const load = async () => {
            try {
                const config = await getAuthConfig(instance, accounts[0])
                const data = await fetchTemplates(config)
                if (mounted) {
                    dispatch(setTemplates(data))
                }
            } catch (error) {
                console.log('Failed to load templates', error)
            }
        }
        if (instance && accounts && accounts[0]) load()
        return () => {
            mounted = false
        }
    }, [instance, accounts, dispatch])

    // Effect: apply default template once per date
    useEffect(() => {
        try {
            resetBaseline()
            const dateISO = selectedDate?.header?.mserp_timeprofiledate ? new Date(selectedDate.header.mserp_timeprofiledate).toISOString() : null

            if (!dateISO) return
            if (!isDayOpen) return // only apply when day is open
            if (appliedDateRef.current === dateISO) return // already applied for this date
            if (!templates || templates.length === 0) return

            const defaultTemplate = templates.find((template: any) => template?.timeCardHeader?.default)
            if (defaultTemplate) {
                const applied = applyTemplateToTimecard(selectedDate, defaultTemplate)
                if (applied) {
                    appliedDateRef.current = dateISO
                    dispatch(setSelectedDate(applied))
                    console.log('default template applied')
                    resetBaseline(applied)
                }
            }
        } catch (err) {
            console.log('Error applying template:', err)
        }
    }, [selectedDate?.header?.mserp_timeprofiledate, templates, isDayOpen, dispatch])

    const handleBreakAmountChange = (fieldName: string, lineNumber: number, amount: number) => {
        if (breaks) {
            if (!isNaN(amount)) {
                const noBreaks: TimeCardLine[] | undefined = selectedDate?.lines?.filter((line: TimeCardLine) => line.mserp_timeandattendancejobid !== ENV_JID_BREAK)

                const breaksCopy = [...breaks]
                breaksCopy[lineNumber] = {
                    ...breaksCopy[lineNumber],
                    [fieldName]: amount,
                }

                updateBreakLines(selectedDate, noBreaks, breaksCopy, dispatch, setSelectedDate)
            }
        }
    }

    const handleLineChange = (jobId: string, fieldName: string, newValue: any) => {
        const newTimeCardLines: TimeCardLine[] | undefined = selectedDate?.lines
        if (newTimeCardLines !== undefined) {
            const newTimeCardLinesCopy = [...newTimeCardLines]

            if (selectedDate?.lines) {
                const index = selectedDate.lines.findIndex((line: TimeCardLine) => line.mserp_timeandattendancejobid === jobId && !line.mserp_oncalltimeandattendancejobid)
                if (jobId === ENV_JID_START || jobId === ENV_JID_STOP) {
                    newTimeCardLinesCopy[index] = {
                        ...newTimeCardLinesCopy[index],
                        mserp_startdatetime: newValue,
                        mserp_stopdatetime: newValue,
                    }
                } else {
                    newTimeCardLinesCopy[index] = {
                        ...newTimeCardLinesCopy[index],
                        [fieldName]: newValue,
                    }
                }

                const newSelectedDate = JSON.parse(JSON.stringify(selectedDate))
                if (newSelectedDate !== undefined && newSelectedDate.lines !== undefined) {
                    newSelectedDate.lines = newTimeCardLinesCopy
                }
                dispatch(setSelectedDate(newSelectedDate))
            }
        }
    }

    const handleHeaderNumberChange = (fieldName: string, newValue: number) => {
        const newSelectedDate = JSON.parse(JSON.stringify(selectedDate))

        if (newSelectedDate !== undefined && newSelectedDate.header !== undefined && newValue !== undefined) {
            newSelectedDate.header[fieldName] = Number(newValue)
        }
        dispatch(setSelectedDate(newSelectedDate))
    }

    const formatDate = () => {
        return i18n.language === 'de'
            ? format(new Date(selectedDate?.header?.mserp_timeprofiledate ?? new Date().toISOString()), 'EEEE, dd.MM.yyyy', {
                  locale: de,
              })
            : format(new Date(selectedDate?.header?.mserp_timeprofiledate ?? new Date().toISOString()), 'EEEE, MM/dd/yyyy', {
                  locale: enUS,
              })
    }

    const customSnackbarMessageSuccess: ICustomSnackbar = {
        message: t('zdeheader.deleteSuccess'),
        type: 'success',
    }
    const customSnackbarMessageError: ICustomSnackbar = {
        message: t('zdeheader.deleteError'),
        type: 'error',
    }

    const scope : string = process.env.REACT_APP_SCOPE || ''
    const accessTokenRequest: SilentRequest = {
        scopes: [scope],
        account: accounts[0],
        forceRefresh: false,
    }

    const deleteTimecard = () => {
        getAuthConfig(instance, accounts[0])
            .then((headers) => {
                const config: AxiosRequestConfig = {
                    ...headers,
                    params: {
                        date: selectedDate?.header?.mserp_timeprofiledate ? format(selectedDate.header.mserp_timeprofiledate, 'yyyy-MM-dd') : '',
                    },
                }
                dispatch(setLoadingDate(true))
                axios
                    .delete(`${process.env.REACT_APP_BACKEND_URL}/Timecard`, config)
                    .then((response) => {
                        if (response.status === 200) {
                            dispatch(setAlert(customSnackbarMessageSuccess))
                            if (selectedDate.header?.mserp_timeprofiledate) {
                                updateDate(selectedDate.header?.mserp_timeprofiledate)
                            } else {
                                dispatch(setLoadingDate(false))
                            }
                            dispatch(setRequireCalendarReload(true))
                        } else {
                            dispatch(setAlert(customSnackbarMessageError))
                            dispatch(setLoadingDate(false))
                        }
                    })
                    .catch(() => {
                        dispatch(setAlert(customSnackbarMessageError))
                        dispatch(setLoadingDate(false))
                    })
            })
            .catch((error) => {
                console.log(error)
                dispatch(setAlert(customSnackbarMessageError))
                dispatch(setLoadingDate(false))
            })
    }

    const updateDate = (date: string) => {
        instance
            .acquireTokenSilent(accessTokenRequest)
            .then((accessTokenResponse) => {
                const accessToken: string = accessTokenResponse.accessToken
                UpdateStoreHelper(format(date, 'yyyy-MM-dd'), accessToken)
            })
            .catch((error) => {
                if (error instanceof InteractionRequiredAuthError) {
                    return instance.acquireTokenRedirect(accessTokenRequest)
                }
                console.log(error)
                dispatch(setLoadingDate(false))
            })
    }

    const addNewBreak = () => {
        const newEmptyLine = {
            mserp_timeprofiledate: selectedDate.header?.mserp_timeprofiledate,
            mserp_workerpersonnelnumber: selectedDate.header?.mserp_workerpersonnelnumber,
            mserp_dataareaid: user.resource.mserp_resourcecompanyid,
            mserp_mbelectronictimecardlineentityid: EmptyGuid,
            mserp_startdatetime: '',
            mserp_stopdatetime: '',
            mserp_linenumber: 0,
            mserp_projectid: '',
            mserp_projectcategoryid: '',
            mserp_timeandattendancejobid: ENV_JID_BREAK,
            mserp_indirectactivitycategoryid: 'BREAK', //TODO change constant id
            mserp_calculatedtimeseconds: 0,
            delete: false,
        }

        const newSelectedDate = JSON.parse(JSON.stringify(selectedDate))
        if (newSelectedDate?.lines) {
            newSelectedDate.lines = [...(selectedDate.lines || []), newEmptyLine]
        }
        dispatch(setSelectedDate(newSelectedDate))
    }

    const deleteBreak = (lineNumber: number) => () => {
        if (breaks) {
            const noBreaks: TimeCardLine[] | undefined = selectedDate?.lines?.filter((line: TimeCardLine) => line.mserp_timeandattendancejobid !== ENV_JID_BREAK)

            const breaksCopy = [...breaks]
            breaksCopy[lineNumber] = {
                ...breaksCopy[lineNumber],
                delete: true,
            }

            updateBreakLines(selectedDate, noBreaks, breaksCopy, dispatch, setSelectedDate)
        }
    }

    const handleOnCallDropdown = (event: SelectChangeEvent<typeof onCallValue>) => {
        const value = event.target.value
        updateOnCallLines(value)
    }

    const updateOnCallLines = (newValue: string = '') => {
        const emptyField = newValue === ''
        const newTimeCardLines: TimeCardLine[] | undefined = selectedDate?.lines

        if (newTimeCardLines) {
            const onCallLineIndexArray: number[] = []
            newTimeCardLines.forEach((line: TimeCardLine, index: number) => {
                if (line.mserp_oncalltimeandattendancejobid === JID_RB) {
                    onCallLineIndexArray.push(index)
                }
            })

            const newTimeCardLinesCopy = [...newTimeCardLines]
            if (onCallLineIndexArray.length > 0) {
                if (!emptyField) {
                    onCallLineIndexArray.forEach((index) => {
                        newTimeCardLinesCopy[index] = {
                            ...newTimeCardLinesCopy[index],
                            mserp_mboncallapplicationid: newValue,
                            delete: false,
                        }
                    })
                } else {
                    onCallLineIndexArray.forEach((index) => {
                        newTimeCardLinesCopy[index] = {
                            ...newTimeCardLinesCopy[index],
                            mserp_mboncallapplicationid: '',
                            delete: true,
                        }
                    })
                }

                const newSelectedDate = JSON.parse(JSON.stringify(selectedDate))
                if (newSelectedDate.lines) {
                    newSelectedDate.lines = newTimeCardLinesCopy
                }
                dispatch(setSelectedDate(newSelectedDate))
            } else if (!emptyField) {
                addNewOnCallLines(newValue)
            }
        }
    }

    const addNewOnCallLines = (value: string) => {
        const currentDate = selectedDate.header?.mserp_timeprofiledate
        const newCurrentDate: Date = new Date(currentDate ?? Date.now())
        newCurrentDate.setHours(0, 0, 0, 0)
        const currentDateTime: string = formatISO(newCurrentDate)

        const newOncallLineClockIn = {
            mserp_timeprofiledate: selectedDate.header?.mserp_timeprofiledate,
            mserp_workerpersonnelnumber: selectedDate.header?.mserp_workerpersonnelnumber,
            mserp_dataareaid: user.resource.mserp_resourcecompanyid,
            mserp_mbelectronictimecardlineentityid: EmptyGuid,
            mserp_startdatetime: currentDateTime,
            mserp_stopdatetime: currentDateTime,
            mserp_linenumber: 0,
            mserp_projectid: '',
            mserp_projectcategoryid: '',
            mserp_timeandattendancejobid: ENV_JID_START,
            mserp_indirectactivitycategoryid: '',
            mserp_calculatedtimeseconds: 0,
            mserp_oncalltimeandattendancejobid: JID_RB,
            mserp_mboncallapplicationid: value,
            delete: false,
        }
        const newOncallLineClockOut = {
            mserp_timeprofiledate: selectedDate.header?.mserp_timeprofiledate,
            mserp_workerpersonnelnumber: selectedDate.header?.mserp_workerpersonnelnumber,
            mserp_dataareaid: user.resource.mserp_resourcecompanyid,
            mserp_mbelectronictimecardlineentityid: EmptyGuid,
            mserp_startdatetime: currentDateTime,
            mserp_stopdatetime: currentDateTime,
            mserp_linenumber: 0,
            mserp_projectid: '',
            mserp_projectcategoryid: '',
            mserp_timeandattendancejobid: ENV_JID_STOP,
            mserp_indirectactivitycategoryid: '',
            mserp_calculatedtimeseconds: 0,
            mserp_oncalltimeandattendancejobid: JID_RB,
            mserp_mboncallapplicationid: '',
            delete: false,
        }

        const newSelectedDate = JSON.parse(JSON.stringify(selectedDate))
        if (newSelectedDate.lines) {
            newSelectedDate.lines = [...(selectedDate.lines || []), newOncallLineClockIn, newOncallLineClockOut]
        }
        dispatch(setSelectedDate(newSelectedDate))
    }

    useEffect(() => {}, [selectedDate, user])

    const formDisabled = selectedDate?.header?.isTransferred || selectedDate?.dayInformation?.mserp_transferred // TODO check whether date is outside of permitted recording period

    let statusValue: string
    if (!selectedDate?.dayInformation?.mserp_transferred) {
        statusValue = selectedDate?.header?.status ?? ''
    } else {
        statusValue = selectedDate.header?.isTransferred ? 'RELEASED' : 'NOTRECORDED'
    }

    const getBreakActionButton = (i: number) => {
        if (i === 0) {
            return !formDisabled ? (
                <Tooltip title={t('zdelines.addbreak')}>
                    <IconButton size="small" onClick={addNewBreak}>
                        <AddCircleIcon />
                    </IconButton>
                </Tooltip>
            ) : (
                <IconButton size="small" onClick={addNewBreak} disabled={true}>
                    <AddCircleIcon />
                </IconButton>
            )
        } else {
            return !formDisabled ? (
                <Tooltip title={t('zdelines.deletebreak')}>
                    <IconButton size="small" onClick={deleteBreak(i)}>
                        <DeleteOutlineIcon />
                    </IconButton>
                </Tooltip>
            ) : (
                <IconButton size="small" onClick={deleteBreak(i)} disabled={true}>
                    <DeleteOutlineIcon />
                </IconButton>
            )
        }
    }

    let addBreakButton
    if (!formDisabled) {
        addBreakButton = (
            <Tooltip title={t('zdelines.addbreak')}>
                <IconButton size="small" onClick={addNewBreak} disabled={formDisabled}>
                    <AddCircleIcon />
                </IconButton>
            </Tooltip>
        )
    } else {
        addBreakButton = (
            <IconButton size="small" onClick={addNewBreak} disabled={formDisabled}>
                <AddCircleIcon />
            </IconButton>
        )
    }

    let travelValue: string | number = Math.floor(Math.round(selectedDate?.header?.mserp_mbquantitytravel ? selectedDate?.header?.mserp_mbquantitytravel * 10000 : 0) / 100) / 100
    if (fixAmountDecimalsTravel) {
        travelValue = travelValue.toFixed(2)
    }

    let onCallTextFieldValue = ''
    if (selectedDate?.oncallRequests) {
        const onCall = selectedDate.oncallRequests.find((item) => item.mserp_mbapplicationid === onCallValue)
        if (onCallValue) {
            onCallTextFieldValue = onCall ? `${onCall.mserp_mbapplicationid} - ${onCall.mserp_notes}` : `${onCallValue}`
        }
    }

    let onCallComponent
    if (!loadingDate && selectedDate?.oncallRequests) {
        if (!formDisabled) {
            onCallComponent = (
                <Select size="small" sx={{ width: 250 }} value={onCallValue ?? ''} onChange={handleOnCallDropdown}>
                    <MenuItem key={''} value={''} selected>
                        {t('zdeheader.removeselection')}
                    </MenuItem>
                    {selectedDate?.oncallRequests.map((oncall: OnCallRequest, i: number) => (
                        <MenuItem key={oncall.mserp_mbapplicationid + `_` + i} value={oncall.mserp_mbapplicationid} selected>
                            {oncall.mserp_mbapplicationid} - {oncall.mserp_notes}
                        </MenuItem>
                    ))}
                </Select>
            )
        } else {
            onCallComponent = <TextField value={onCallTextFieldValue} size="small" disabled={true} />
        }
    } else {
        onCallComponent = <Skeleton variant="text" width="50%" />
    }

    return user?.resource ? (
        <>
            <Box display="flex" flexWrap="wrap" alignItems="end" gap="16px" justifyContent="space-between" width="100%">
                {!loadingDate && user ? (
                    <Box display="flex" flexWrap="wrap" gap="10px" alignItems="center">
                        <Typography variant="h4">
                            {formatDate()} | ({user?.resource?.mserp_resourceid})
                        </Typography>
                        <StatusChip status={statusValue} />
                        {selectedDate.header?.status === 'RELEASED' ? (
                            <Tooltip
                                title={
                                    <>
                                        <span>&#8226; {t('zdeheader.hourscalculatedinfo1')}</span>
                                        <br />
                                        <span>&#8226; {t('zdeheader.hourscalculatedinfo2')}</span>
                                    </>
                                }
                                placement="bottom"
                            >
                                <Chip
                                    label={!selectedDate?.dayInformation?.mserp_transferred ? t('zdeheader.hourscalculationpending') : t('zdeheader.hourscalculated')}
                                    color="default"
                                    variant="outlined"
                                    icon={
                                        !selectedDate?.dayInformation?.mserp_transferred ? (
                                            <HistoryToggleOffIcon color="action" sx={{ width: { xs: '0.9em', sm: '1em' }, height: { xs: '0.9em', sm: '1em' } }} />
                                        ) : (
                                            <CheckCircleIcon color="success" sx={{ width: { xs: '0.9em', sm: '1em' }, height: { xs: '0.9em', sm: '1em' } }} />
                                        )
                                    }
                                />
                            </Tooltip>
                        ) : (
                            <></>
                        )}
                    </Box>
                ) : (
                    <Typography variant="h4">
                        <Skeleton variant="text" sx={{ width: { xs: 200, md: 300 }, display: 'inline-block' }} />
                    </Typography>
                )}
                <Box display="flex" justifyContent="flex-end" alignItems="center" gap={2}>
                    {selectedDate?.header?.mserp_timeprofiledate && new Date(selectedDate.header.mserp_timeprofiledate + 'Z') >= new Date('2026-01-01') && (
                        <Tooltip title={t('template.tooltip_title')}>
                            <Button variant="outlined" onClick={() => setOpenTemplateDialog(true)} startIcon={<ContentPasteGoIcon />} color="primary">
                                {t('template.title')}
                            </Button>
                        </Tooltip>
                    )}
                    <Tooltip title={t('zdeheader.resetDayTitle')}>
                        <Button variant="outlined" onClick={deleteTimecard} startIcon={<RestartAltIcon />} color="error" disabled={formDisabled || loadingDate}>
                            {t('zdeheader.resetDay')}
                        </Button>
                    </Tooltip>
                </Box>
            </Box>
            {openTemplateDialog && <Template onClose={() => setOpenTemplateDialog(false)} isDayOpen={isDayOpen} />}
            <Divider sx={{ marginY: '8px' }} />

            <Grid container spacing={1} alignItems="center">
                <Grid size={{ xs: 12, sm: 5, md: 4 }}>
                    <Typography variant="body1">{t('zdeheader.workingtime')}</Typography>
                </Grid>
                <Grid size={{ xs: 12, sm: 7, md: 8 }} marginBottom="8px">
                    {!loadingDate ? (
                        <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={i18n.language === 'de' ? de : enUS}>
                            <DesktopTimePicker
                                views={['hours', 'minutes']}
                                ampm={false}
                                slotProps={{
                                    textField: { size: 'small' },
                                    openPickerButton: { tabIndex: -1 },
                                }}
                                sx={{ width: '120px' }}
                                value={fromTimeDate}
                                onChange={handleDateChange(ENV_JID_START, 'mserp_startdatetime')}
                                disabled={formDisabled}
                            />
                            <Typography display="inline-block" variant="body1" sx={{ lineHeight: '40px' }} px={2}>
                                {t('zdeheader.to')}
                            </Typography>
                            <DesktopTimePicker
                                views={['hours', 'minutes']}
                                ampm={false}
                                slotProps={{
                                    textField: { size: 'small' },
                                    openPickerButton: { tabIndex: -1 },
                                }}
                                sx={{ width: '120px' }}
                                value={toTimeDate}
                                onChange={handleDateChange(ENV_JID_STOP, 'mserp_startdatetime')}
                                disabled={formDisabled}
                            />
                        </LocalizationProvider>
                    ) : (
                        <Skeleton variant="text" width="50%" />
                    )}
                </Grid>

                <Grid size={{ xs: 12, sm: 5, md: 4 }}>
                    <Typography>{t('zdeheader.break')}</Typography>
                </Grid>

                <Grid size={{ xs: 12, sm: 7, md: 8 }} marginBottom="8px">
                    {!loadingDate ? (
                        <>
                            {breaks && breaks?.length > 0
                                ? breaks?.map((element: TimeCardLine, i: number) =>
                                      !element.delete ? (
                                          <Box paddingTop={i !== 0 ? 2 : 0} key={element.mserp_mbelectronictimecardlineentityid || `break-${i}-${element.mserp_linenumber ?? ''}`} display="flex" alignItems="center" gap="6px">
                                              {(() => {
                                                  let breakValue: string | number = Math.floor(Math.round(element.mserp_calculatedtimeseconds ? (element.mserp_calculatedtimeseconds * 100) / 36 : 0) / 100) / 100
                                                  if (fixAmountDecimalsBreaks) {
                                                      breakValue = breakValue.toFixed(2)
                                                  }
                                                  return (
                                                      <TextField
                                                          value={breakValue}
                                                          type="number"
                                                          size="small"
                                                          sx={{ width: '120px' }}
                                                          slotProps={{
                                                              htmlInput: { style: { textAlign: 'center' }, min: 0, step: 0.25 },
                                                              input: {
                                                                  endAdornment: <InputAdornment position="start">h</InputAdornment>,
                                                              },
                                                          }}
                                                          onBlur={() => setFixAmountDecimalsBreaks(true)}
                                                          onChange={(e: any) => {
                                                              setFixAmountDecimalsBreaks(false)
                                                              if (String(e.target.value).charAt(0) === '0' && String(e.target.value).length > 1 && String(e.target.value).charAt(1) !== '.') {
                                                                  e.target.value = '0.' + String(e.target.value).substring(1)
                                                              }
                                                              handleBreakAmountChange('mserp_calculatedtimeseconds', i, Math.floor(Number((e.target.value * 100).toFixed(1))) * 36)
                                                          }}
                                                          disabled={formDisabled}
                                                      />
                                                  )
                                              })()}
                                              {getBreakActionButton(i)}
                                          </Box>
                                      ) : (
                                          <></>
                                      )
                                  )
                                : addBreakButton}
                        </>
                    ) : (
                        <Skeleton variant="text" width="50%" />
                    )}
                </Grid>
                <Grid size={{ xs: 12, sm: 5, md: 4 }}>
                    <Typography>{t('zdeheader.traveltime')}</Typography>
                </Grid>
                <Grid size={{ xs: 12, sm: 7, md: 8 }} marginBottom="8px">
                    {!loadingDate ? (
                        <TextField
                            value={travelValue}
                            onBlur={() => setFixAmountDecimalsTravel(true)}
                            onChange={(e: any) => {
                                setFixAmountDecimalsTravel(false)
                                if (String(e.target.value).charAt(0) === '0' && String(e.target.value).length > 1 && String(e.target.value).charAt(1) !== '.') {
                                    e.target.value = '0.' + String(e.target.value).substring(1)
                                }
                                handleHeaderNumberChange('mserp_mbquantitytravel', Math.floor(Number((e.target.value * 100).toFixed(1))) / 100)
                            }}
                            type="number"
                            size="small"
                            sx={{ width: '120px' }}
                            slotProps={{
                                htmlInput: { style: { textAlign: 'center' }, min: 0, step: 0.25 },
                                input: {
                                    endAdornment: <InputAdornment position="start">h</InputAdornment>,
                                },
                            }}
                            disabled={formDisabled}
                        />
                    ) : (
                        <Skeleton variant="text" width="50%" />
                    )}
                </Grid>

                <Grid size={{ xs: 12, sm: 5, md: 4 }}>
                    <Typography>{t('zdeheader.workingtimeAccordingArbZG')}</Typography>
                </Grid>
                <Grid size={{ xs: 12, sm: 7, md: 8 }} marginBottom="8px">
                    {!loadingDate ? (
                        <TextField
                            type="number"
                            disabled
                            size="small"
                            value={workTimeAccordingArbZG.toFixed(2)}
                            sx={{ width: '120px' }}
                            slotProps={{
                                htmlInput: { style: { textAlign: 'center' }, min: 0 },
                                input: {
                                    endAdornment: <InputAdornment position="start">h</InputAdornment>,
                                },
                            }}
                        />
                    ) : (
                        <Skeleton variant="text" width="50%" />
                    )}
                </Grid>

                {onCallAllowed && (
                    <>
                        <Grid size={{ xs: 12, sm: 5, md: 4 }}>
                            <Typography>{t('zdeheader.oncall')}</Typography>
                        </Grid>
                        <Grid size={{ xs: 12, sm: 7, md: 8 }} marginBottom="8px">
                            {onCallComponent}
                        </Grid>
                    </>
                )}
            </Grid>
            <br></br>
            <Typography variant="h4">{t('zdeheader.timeBooking')}</Typography>
            <Divider sx={{ marginTop: '8px' }} />
            <ZDELines />
            <Box pt={5}>
                <ViolationComment />
            </Box>
        </>
    ) : (
        <></>
    )
}

export default ZDEHeader
